import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { BookingPeriod, Game, GroupedGames } from '../types/types';

interface UseGamesReturn {
  games: GroupedGames;
  gamesById: { [gameId: string]: Game };
  loading: boolean;
  error: string;
}

export const useGames = (bookingPeriod: BookingPeriod | null): UseGamesReturn => {
  const [games, setGames] = useState<GroupedGames>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const fetchGames = async () => {
      if (!bookingPeriod) return;
      
      setLoading(true);
      const apiUrl = bookingPeriod.api_url;
      let fetchedGames: Game[] = [];
      
      try {
        if (bookingPeriod.api_url2) {
          // Execute both API calls concurrently
          const [response1, response2] = await Promise.all([
            axios.get<Game[]>(apiUrl),
            axios.get<Game[]>(bookingPeriod.api_url2)
          ]);

          // Merge and sort games
          fetchedGames = [...response1.data, ...response2.data]
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime());
        } else {
          const response = await axios.get<Game[]>(apiUrl);
          fetchedGames = response.data;
        }

        // Filter games by booking period dates
        const startDate = new Date(bookingPeriod.start_day);
        const endDate = new Date(bookingPeriod.end_day);

        const filteredGames = fetchedGames.filter((game) => {
          const gameDate = new Date(game.start);
          return gameDate >= startDate && gameDate <= endDate;
        });

        // Group games by date
        const grouped: GroupedGames = filteredGames.reduce((acc, game) => {
          const gameDate = new Date(game.start);
          const dateKey = gameDate.toISOString().split('T')[0];
          game = { ...game, date: dateKey };
          if (!acc[dateKey]) {
            acc[dateKey] = [];
          }
          acc[dateKey].push(game);
          return acc;
        }, {} as GroupedGames);

        setGames(grouped);
      } catch (err: any) {
        setError(`Failed to fetch games: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchGames();
  }, [bookingPeriod]);

  const gamesById = useMemo(() => {
    const map: { [gameId: string]: Game } = {};
    Object.values(games).forEach((gamesList) => {
      gamesList.forEach((game) => {
        map[game.id] = game;
      });
    });
    return map;
  }, [games]);

  return {
    games,
    gamesById,
    loading,
    error,
  };
};
